import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CallAttempt,
  DialogTranscription,
} from '../../../models/CallAttempt/CallAttempt';
import {
  CallDetailsViewModel,
  createTaskDetailFallbackViewModel,
} from '../../../models/Task/callDetailsViewModel';
import { callDetailsApi } from '../../../services/callDetailsApi';

export type OpenModalParams = {
  taskId: string;
  callType: 'outgoing' | 'incoming';
  incomingCallId?: string;
  taskType?: string | null;
  taskTypeLabel?: string | null;
};

export const useCallDetailsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [selectedTaskTypeLabel, setSelectedTaskTypeLabel] = useState<
    string | null
  >(null);
  const [callType, setCallType] = useState<'outgoing' | 'incoming'>('outgoing');
  const [incomingCallId, setIncomingCallId] = useState<string | null>(null);
  const [callAttempts, setCallAttempts] = useState<CallAttempt[]>([]);
  const [isLoadingAttempts, setIsLoadingAttempts] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [callDetails, setCallDetails] = useState<CallDetailsViewModel | null>(
    null,
  );
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [transcriptions, setTranscriptions] = useState<
    Record<string, DialogTranscription[]>
  >({});
  const [transcriptionLoading, setTranscriptionLoading] = useState<
    Record<string, boolean>
  >({});
  const [transcriptionAvailability, setTranscriptionAvailability] = useState<
    Record<string, boolean>
  >({});

  const audioUrlsRef = useRef<Record<string, string>>({});
  const transcriptionsRef = useRef<Record<string, boolean>>({});

  const clearTrackedAudioUrls = useCallback(() => {
    Object.values(audioUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });

    audioUrlsRef.current = {};
  }, []);

  const resetLoadedData = useCallback(() => {
    clearTrackedAudioUrls();
    setAudioUrls({});
    setTranscriptions({});
    setTranscriptionLoading({});
    setTranscriptionAvailability({});
    setCallAttempts([]);
    setLoadingError(null);
    setCallDetails(null);
    setIsLoadingDetails(false);
    setDetailsError(null);
    transcriptionsRef.current = {};
  }, [clearTrackedAudioUrls]);

  const openModal = useCallback(
    (params: OpenModalParams) => {
      resetLoadedData();
      setSelectedTaskId(params.taskId);
      setSelectedTaskType(params.taskType ?? null);
      setSelectedTaskTypeLabel(params.taskTypeLabel ?? null);
      setCallType(params.callType);
      setIncomingCallId(
        params.callType === 'incoming' ? (params.incomingCallId ?? null) : null,
      );
      setIsLoadingAttempts(true);
      setIsOpen(true);
    },
    [resetLoadedData],
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSelectedTaskId(null);
    setSelectedTaskType(null);
    setSelectedTaskTypeLabel(null);
    setCallType('outgoing');
    setIncomingCallId(null);
    setIsLoadingAttempts(false);
    resetLoadedData();
  }, [resetLoadedData]);

  useEffect(() => {
    return () => {
      clearTrackedAudioUrls();
    };
  }, [clearTrackedAudioUrls]);

  useEffect(() => {
    let isCancelled = false;

    const fetchCallAttempts = async () => {
      if (!isOpen || !selectedTaskId) {
        return;
      }

      setIsLoadingAttempts(true);
      setIsLoadingDetails(true);
      setLoadingError(null);
      setDetailsError(null);

      try {
        const attempts = await callDetailsApi.getCallAttempts(selectedTaskId);

        if (!isCancelled) {
          setCallAttempts(attempts);
        }

        try {
          const details = await callDetailsApi.getTaskDetail(
            selectedTaskId,
            selectedTaskType,
            attempts,
            selectedTaskTypeLabel,
          );

          if (!isCancelled) {
            setCallDetails(details);
            if (details.detailLoadState === 'failed') {
              setDetailsError('Failed to load task details.');
            }
          }
        } catch (error) {
          if (!isCancelled) {
            setCallDetails(
              createTaskDetailFallbackViewModel({
                taskId: selectedTaskId,
                taskType: selectedTaskType,
                callAttempts: attempts,
                detailLoadState: 'failed',
                isSupported: true,
              }),
            );
            setDetailsError('Failed to load task details.');
          }
        }
      } catch (error) {
        if (!isCancelled) {
          setCallAttempts([]);
          setLoadingError('Failed to load call attempts.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingAttempts(false);
          setIsLoadingDetails(false);
        }
      }
    };

    void fetchCallAttempts();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, selectedTaskId, selectedTaskType, selectedTaskTypeLabel]);

  const preloadTranscriptionAvailability = useCallback(
    async (attempts: CallAttempt[]) => {
      if (attempts.length === 0) return;

      const ids = attempts.map((a) => a.id);

      const results = await Promise.allSettled(
        ids.map(async (id) => {
          const data = await callDetailsApi.getTranscription(id);
          return { id, hasTranscription: data.length > 0, data };
        }),
      );

      const availability: Record<string, boolean> = {};
      const preloaded: Record<string, DialogTranscription[]> = {};

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const { id, hasTranscription, data } = result.value;
          availability[id] = hasTranscription;
          if (hasTranscription) {
            preloaded[id] = data;
            transcriptionsRef.current[id] = true;
          }
        }
      }

      setTranscriptionAvailability((prev) => ({ ...prev, ...availability }));
      setTranscriptions((prev) => ({ ...prev, ...preloaded }));
    },
    [],
  );

  useEffect(() => {
    if (!isOpen || callAttempts.length === 0) return;
    void preloadTranscriptionAvailability(callAttempts);
  }, [isOpen, callAttempts, preloadTranscriptionAvailability]);

  const loadAudio = useCallback(
    async (id: string) => {
      if (audioUrlsRef.current[id]) {
        return;
      }

      try {
        setLoadingError(null);

        const audioUrl = await callDetailsApi.getRecording(id);

        const previousAudioUrl = audioUrlsRef.current[id];

        if (previousAudioUrl) {
          URL.revokeObjectURL(previousAudioUrl);
        }

        audioUrlsRef.current = {
          ...audioUrlsRef.current,
          [id]: audioUrl,
        };

        setAudioUrls((previousAudioUrls) => ({
          ...previousAudioUrls,
          [id]: audioUrl,
        }));
      } catch (error) {
        console.error('Failed to load audio.', error);
        setLoadingError('Failed to load audio.');
      }
    },
    [callType],
  );

  const loadTranscription = useCallback(
    async (id: string) => {
      if (transcriptionsRef.current[id]) {
        return;
      }

      transcriptionsRef.current[id] = true;

      setTranscriptionLoading((previousState) => ({
        ...previousState,
        [id]: true,
      }));

      try {
        setLoadingError(null);

        const transcription = await callDetailsApi.getTranscription(id);

        setTranscriptions((previousTranscriptions) => ({
          ...previousTranscriptions,
          [id]: transcription,
        }));
      } catch (error) {
        console.error('Failed to load transcription.', error);
        setLoadingError('Failed to load transcription.');
        transcriptionsRef.current[id] = false;
      } finally {
        setTranscriptionLoading((previousState) => ({
          ...previousState,
          [id]: false,
        }));
      }
    },
    [callType],
  );

  return {
    isOpen,
    openModal,
    closeModal,
    callAttempts,
    isLoadingAttempts,
    loadingError,
    callType,
    incomingCallId,
    audioUrls,
    loadAudio,
    transcriptions,
    loadTranscription,
    transcriptionLoading,
    transcriptionAvailability,
    callDetails,
    isLoadingDetails,
    detailsError,
  };
};
